/* mealCard.js
 * MealCard — renders one product card, manages selection & diet-chip state.
 * Listens to the 'onDurationChange' CustomEvent (fired by MealPlanner) to
 * update the price display in-place without a full re-render.
 */

class MealCard {
  constructor({ mealItem, mode, isSelected, onToggle, onDietChange, onQuantityChange, resolvedSubProducts = [] }) {
    this.mealItem             = mealItem;          // MealItem instance (shared pool, owned by MealPlanner)
    this.mode                 = mode;              // 'radio' | 'multi'
    this.isSelected           = isSelected;
    this.onToggle             = onToggle;          // (handle, nowSelected) => void
    this.onDietChange         = onDietChange;      // (handle, newPref)    => void
    this.onQuantityChange     = onQuantityChange;  // ()                   => void
    this.resolvedSubProducts  = resolvedSubProducts; // [{handle,label,imgSrc,bodyTxt,perMealPrice}]
    this.el                   = null;              // set by render()

    // Bound once so the same reference can be removed in destroy()
    this._onDurationChange = this._handleDurationChange.bind(this);
    document.addEventListener('onDurationChange', this._onDurationChange);
  }

  // ─── onDurationChange listener ───────────────────────────────────────────────
  // MealPlanner.changeDuration() already synced all MealItem durations before
  // firing the event, so we only need to refresh the price display here.
  _handleDurationChange(/* e */) {
    this._updatePriceDisplay();
  }

  // ─── Diet-option detection ────────────────────────────────────────────────────
  _getDietOptions() {
    const DIET_KEYWORDS = ['veg', 'egg', 'non-veg', 'nonveg', 'non veg', 'vegetarian'];

    // Fixed display order: Veg → Egg → Non-Veg
    const ORDER = ['veg', 'vegetarian', 'egg', 'non-veg', 'nonveg', 'non veg'];

    const opt = (this.mealItem.product?.options || []).find(o =>
      (o.values || []).some(v => DIET_KEYWORDS.includes(v.trim().toLowerCase()))
    );
    if (!opt) return [];

    return [...(opt.values || [])].sort((a, b) => {
      const ia = ORDER.indexOf(a.trim().toLowerCase());
      const ib = ORDER.indexOf(b.trim().toLowerCase());
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }

  // ─── Price HTML fragment ──────────────────────────────────────────────────────
  // Uses config price string first; falls back to per_meal_price from metafield.
  _priceHtml() {
    const configPrice = this.mealItem.configProd?.price;
    if (configPrice) {
      return `<span class="mpb-card-price-value">${configPrice}</span>`;
    }
    const perMeal = this.mealItem.getPerMealPrice();
    if (perMeal !== null) {
      return `<span class="mpb-card-price-value">₹${perMeal.toLocaleString('en-IN')}</span>
              <span class="mpb-card-price-unit">/Meal</span>`;
    }
    return `<span class="mpb-card-price-value mpb-card-price-na">—</span>`;
  }

  // ─── Render ───────────────────────────────────────────────────────────────────
  render() {
    const { mealItem, mode, isSelected } = this;
    const p         = mealItem.product;
    const title     = mealItem.configProd?.displayName || mealItem.configProd?.title || p?.title || mealItem.handle;
    const imgSrc    = p?.featured_image || p?.images?.[0]?.src || '';
    const desc      = mealItem.configProd?.description
      || window.MPB_METAFIELDS?.[mealItem.handle]?.metafields?.short_description
      || (p?.body_html ? p.body_html.replace(/<[^>]+>/g, '').trim() : '');
    const delivery  = mealItem.configProd?.delivery || null;
    const dietOpts  = this._getDietOptions();
    const isMulti   = (mode === 'multi');

    const tags       = mealItem.configProd?.tags || [];
    const hasTags    = tags.length > 0;

    const DESC_LIMIT = 65;
    const hasMore    = !hasTags && desc.length > DESC_LIMIT;
    const descShort  = hasMore ? desc.substring(0, DESC_LIMIT) : desc;

    const div = document.createElement('div');
    div.className      = `mpb-meal-card${isSelected ? ' selected' : ''} mpb-card-${mode}`;
    div.dataset.handle = mealItem.handle;

    const configProd    = mealItem.configProd;
    const retInfo       = mealItem.getRetentionInfo?.() || null;

    // ── Quantity chips ───────────────────────────────────────────────────────
    // Priority: retention_info.quantity_multiplier > configProd.quantityOptions
    let qtyHtml = '';
    if (isSelected) {
      const retQty = retInfo?.quantity_multiplier;
      const cfgQty = configProd?.quantityOptions;
      const qtyLabel = configProd?.quantityLabel || 'Quantity Per Day';

      if (retQty && Object.keys(retQty).length) {
        // Metafield-driven chips — same data source as nutripack.liquid
        const entries = Object.entries(retQty);
        qtyHtml = `<div class="mpb-qty-section">
          <div class="mpb-qty-label">${qtyLabel}</div>
          <div class="mpb-qty-chips">
            ${entries.map(([key, opt]) => {
              const mult  = (typeof opt === 'object') ? opt.multiplier : opt;
              let lbl     = (typeof opt === 'object') ? opt.label      : String(opt);
              // Strip "/day", "/ day", "/Day", " / Day", "per day", etc. to show clean quantity
              lbl = lbl.replace(/\s*(?:\/|per)\s*(?:per\s+)?day\s*$/i, '').trim();
              const isAct = mealItem.selectedQuantity === mult;
              return `<button class="mpb-qty-chip${isAct ? ' active' : ''}" data-qty="${mult}">${lbl}</button>`;
            }).join('')}
          </div>
        </div>`;
      } else if (cfgQty?.length) {
        // Config-driven chips (fallback)
        qtyHtml = `<div class="mpb-qty-section">
          <div class="mpb-qty-label">${qtyLabel}</div>
          <div class="mpb-qty-chips">
            ${cfgQty.map(q =>
              `<button class="mpb-qty-chip${mealItem.selectedQuantity === q ? ' active' : ''}" data-qty="${q}">${q}</button>`
            ).join('')}
          </div>
        </div>`;
      }
    }

    // ── Delivery time chips (from retention_info.delivery_time) ─────────────
    // [DELIVERY-UI HIDDEN] Delivery time section is hidden — the default is
    // set automatically via configProd.defaultDeliveryTime (see mealPlanner.js
    // _handleCardToggle). Re-enable by uncommenting the block below.
    let dtHtml = '';
    /* [DELIVERY-UI HIDDEN]
    if (isSelected) {
      const retDt = retInfo?.delivery_time;
      if (retDt && Object.keys(retDt).length) {
        dtHtml = `<div class="mpb-dt-section">
          <div class="mpb-dt-label">Delivery Time
            <button class="mpb-dt-info-btn" type="button" aria-label="Delivery time info">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.8" width="14" height="14" aria-hidden="true">
                <circle cx="10" cy="10" r="8.5"/>
                <line x1="10" y1="9" x2="10" y2="14"/>
                <circle cx="10" cy="6.5" r="0.8" fill="currentColor" stroke="none"/>
              </svg>
            </button>
            <div class="mpb-dt-tooltip" role="tooltip" hidden>
              Choose when you'd like your meals delivered each day.
            </div>
          </div>
          <div class="mpb-dt-chips">
            ${Object.entries(retDt).map(([key, val]) =>
              `<button class="mpb-dt-chip${mealItem.selectedDeliveryTime === key ? ' active' : ''}" data-dt-key="${key}">${val}</button>`
            ).join('')}
          </div>
        </div>`;
      }
    }
    [END DELIVERY-UI HIDDEN] */

    const dietChipsHtml = dietOpts.map(t =>
      `<button class="mpb-diet-chip${isSelected && mealItem.dietaryPref === t ? ' active' : ''}"
        data-handle="${mealItem.handle}" data-tag="${t}"
        ${!isSelected ? 'disabled' : ''}
        aria-disabled="${!isSelected}">${t}</button>`
    ).join('');

    const SUB_DESC_LIMIT = 55;
    // If the card has qty options, user must pick qty first before seeing sub-options.
    // If no qty options (e.g. Protein Smoothie), show sub-options immediately on selection.
    const hasQtyOptions = !!(configProd?.quantityOptions?.length);
    const showSubProds  = isSelected && (mealItem.selectedQuantity || !hasQtyOptions) && this.resolvedSubProducts.length;
    const subProdsHtml = showSubProds
      ? `<div class="mpb-sub-products-section">
           <div class="mpb-sub-products-heading">What would You Prefer</div>
           <div class="mpb-sub-products-list">
             ${this.resolvedSubProducts.map(sp => {
               const isChosen   = mealItem.selectedSubProduct === sp.handle;
               const spTags     = sp.tags || [];
               const tagsHtml   = spTags.length
                 ? `<div class="mpb-sub-card-tags">
                      ${spTags.map(t => `<span class="mpb-tag-pill">${t}</span>`).join('')}
                    </div>`
                 : '';
               const descHtml   = sp.bodyTxt
                 ? `<div class="mpb-sub-card-desc">${sp.bodyTxt}</div>`
                 : '';
               const priceHtml  = sp.configPrice
                 ? `<div class="mpb-sub-card-price"><span class="mpb-sub-price">${sp.configPrice}</span></div>`
                 : (sp.perMealPrice !== null && !isNaN(sp.perMealPrice))
                   ? `<div class="mpb-sub-card-price"><span class="mpb-sub-price">₹${sp.perMealPrice.toLocaleString('en-IN')}/Day</span></div>`
                   : '';
               const subDeliveryHtml = sp.delivery
                 ? `<div class="mpb-sub-card-delivery">${sp.delivery}</div>`
                 : '';
               return `
                 <div class="mpb-sub-product-card${isChosen ? ' selected' : ''}" data-sub-handle="${sp.handle}">
                   <div class="mpb-sub-card-header">
                     <div class="mpb-sub-card-title-row">
                       <div class="mpb-sub-radio${isChosen ? ' checked' : ''}"></div>
                       <div class="mpb-sub-card-title">
                         ${sp.label}
                         ${sp.showInfo ? `<button class="mpb-info-btn" type="button" aria-label="More info" data-handle="${sp.handle}">?</button>` : ''}
                       </div>
                     </div>
                     ${sp.imgSrc ? `<div class="mpb-sub-card-img"><img src="${sp.imgSrc}" alt="${sp.label}" loading="lazy"></div>` : ''}
                   </div>
                   ${tagsHtml}
                   ${descHtml}
                   ${subDeliveryHtml}
                   ${priceHtml}
                 </div>`;
             }).join('')}
           </div>
         </div>`
      : '';

    div.innerHTML = `
      <div class="mpb-card-image">
        ${imgSrc
          ? `<img src="${imgSrc}" alt="${title}" loading="lazy">`
          : `<div class="mpb-card-image-placeholder">
               <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                 <rect x="3" y="3" width="18" height="18" rx="2"/>
                 <circle cx="8.5" cy="8.5" r="1.5"/>
                 <path d="M21 15l-5-5L5 21"/>
               </svg>
               <span>Image</span>
             </div>`}
      </div>

      ${isMulti
        ? `<div class="mpb-card-check${isSelected ? ' checked' : ''}"></div>`
        : `<div class="mpb-card-select"></div>`}

      <div class="mpb-card-body">
        <div class="mpb-card-name">${title}</div>
        ${!hasTags && desc
          ? `<div class="mpb-card-desc">
               <span class="mpb-desc-text">${descShort}</span>${hasMore
                 ? `<span class="mpb-desc-ellipsis">..... </span><span class="mpb-more-link">More</span>
                    <span class="mpb-desc-full" hidden>${desc}</span>`
                 : ''}
             </div>`
          : ''}
        ${!hasTags && delivery ? `<div class="mpb-card-delivery">${delivery}</div>` : ''}
      </div>

      ${hasTags
        ? `<div class="mpb-card-tags-section">
             <div class="mpb-card-tags">
               ${tags.map(t => `<span class="mpb-tag-pill">${t}</span>`).join('')}
             </div>
             ${desc ? `<div class="mpb-card-desc mpb-card-desc--plain">${desc}</div>` : ''}
             ${delivery ? `<div class="mpb-card-delivery">${delivery}</div>` : ''}
           </div>`
        : ''}

      ${qtyHtml}

      ${dtHtml}

      ${subProdsHtml}

      <div class="mpb-card-bottom-row">
        <div class="mpb-card-diet${!isSelected ? ' mpb-diet-locked' : ''}">
          ${dietChipsHtml}
        </div>
        <div class="mpb-card-price-display">
          ${this._priceHtml()}
        </div>
      </div>`;

    this.el = div;
    this._bindEvents();
    return div;
  }

  // ─── DOM event bindings ────────────────────────────────────────────────────────
  _bindEvents() {
    if (!this.el) return;

    this.el.addEventListener('click', e => {
      if (e.target.closest('.mpb-diet-chip') || e.target.closest('.mpb-qty-chip') ||
          e.target.closest('.mpb-dt-chip') || e.target.closest('.mpb-sub-product-card') ||
          e.target.closest('.mpb-more-link')) return;
      const next = !this.isSelected;
      if (!next) this.mealItem.clearDietaryPref();
      this.onToggle?.(this.mealItem.handle, next);
    });

    // Quantity chip click → set/toggle qty, notify planner for re-render
    this.el.querySelectorAll('.mpb-qty-chip').forEach(chip =>
      chip.addEventListener('click', e => {
        e.stopPropagation();
        const qty = parseInt(chip.dataset.qty, 10);
        this.mealItem.setQuantity(qty);
        this.onQuantityChange?.(this.mealItem.handle, this.mealItem.selectedQuantity);
      })
    );

    // Delivery time chip click → set/toggle, trigger re-render
    this.el.querySelectorAll('.mpb-dt-chip').forEach(chip =>
      chip.addEventListener('click', e => {
        e.stopPropagation();
        this.mealItem.setDeliveryTime(chip.dataset.dtKey);
        this.onQuantityChange?.();
      })
    );

    // [DELIVERY-UI HIDDEN] Info-button tooltip handler — re-enable alongside
    // the delivery time section HTML above if you restore the UI.
    /* [DELIVERY-UI HIDDEN]
    const dtInfoBtn = this.el.querySelector('.mpb-dt-info-btn');
    if (dtInfoBtn) {
      dtInfoBtn.addEventListener('click', e => {
        e.stopPropagation();
        const tooltip = dtInfoBtn.nextElementSibling;
        if (!tooltip) return;
        const isHidden = tooltip.hidden;
        // close any other open tooltips
        document.querySelectorAll('.mpb-dt-tooltip').forEach(t => { t.hidden = true; });
        tooltip.hidden = !isHidden;
      });
      // close on outside click
      document.addEventListener('click', () => {
        const tooltip = dtInfoBtn.nextElementSibling;
        if (tooltip) tooltip.hidden = true;
      }, { capture: true, passive: true });
    }
    [END DELIVERY-UI HIDDEN] */

    // Info (?) button → open more_info modal
    this.el.querySelectorAll('.mpb-info-btn').forEach(btn =>
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const handle  = btn.dataset.handle;
        const raw     = window.MPB_METAFIELDS?.[handle]?.metafields?.product_details;
        const details = (typeof raw === 'string') ? (() => { try { return JSON.parse(raw); } catch { return null; } })() : raw;
        const moreInfo = details?.more_info;
        if (moreInfo) {
          MealCard._openInfoModal(moreInfo);
        } else {
          console.warn('[MPB] more_info not found for handle:', handle, 'details:', details);
        }
      })
    );

    // Sub-product card click → set selection, trigger re-render
    this.el.querySelectorAll('.mpb-sub-product-card').forEach(card =>
      card.addEventListener('click', e => {
        e.stopPropagation();
        this.mealItem.setSubProduct(card.dataset.subHandle);
        this.onQuantityChange?.();
      })
    );

    // Diet chip click → set/toggle preference, refresh chip + price
    this.el.querySelectorAll('.mpb-diet-chip').forEach(chip =>
      chip.addEventListener('click', e => {
        e.stopPropagation();
        this.mealItem.setDietaryPref(chip.dataset.tag);
        this.onDietChange?.(this.mealItem.handle, this.mealItem.dietaryPref);
        this._refreshDietChips();
        this._updatePriceDisplay();
      })
    );

    // "More" link → expand main card description inline
    const moreLink = this.el.querySelector('.mpb-more-link');
    if (moreLink) {
      moreLink.addEventListener('click', e => {
        e.stopPropagation();
        const descEl  = this.el.querySelector('.mpb-card-desc');
        const textEl  = descEl?.querySelector('.mpb-desc-text');
        const ellEl   = descEl?.querySelector('.mpb-desc-ellipsis');
        const fullEl  = descEl?.querySelector('.mpb-desc-full');
        if (textEl && fullEl) {
          textEl.textContent = fullEl.textContent;
          ellEl?.remove();
          moreLink.remove();
          fullEl.remove();
        }
      });
    }

    // "More" link → expand sub-product description inline
    this.el.querySelectorAll('.mpb-sub-more-link').forEach(link => {
      link.addEventListener('click', e => {
        e.stopPropagation();
        const descEl = link.closest('.mpb-sub-card-desc');
        const textEl = descEl?.querySelector('.mpb-sub-desc-text');
        const ellEl  = descEl?.querySelector('.mpb-sub-desc-ellipsis');
        const fullEl = descEl?.querySelector('.mpb-sub-desc-full');
        if (textEl && fullEl) {
          textEl.textContent = fullEl.textContent;
          ellEl?.remove();
          link.remove();
          fullEl.remove();
        }
      });
    });
  }

  // ─── In-place DOM updates ─────────────────────────────────────────────────────

  // Called by MealPlanner after a card toggle to sync visual selection state
  updateSelected(isSelected) {
    this.isSelected = isSelected;
    if (!this.el) return;

    this.el.classList.toggle('selected', isSelected);

    const check = this.el.querySelector('.mpb-card-check');
    if (check) check.classList.toggle('checked', isSelected);

    if (!isSelected) {
      this.mealItem.clearDietaryPref();
      this.mealItem.clearQuantity();
    }
    this._refreshDietChips();
  }

  // Refreshes diet chip active/locked state without a full re-render
  _refreshDietChips() {
    if (!this.el) return;
    const locked   = !this.isSelected;
    const dietWrap = this.el.querySelector('.mpb-card-bottom-row .mpb-card-diet');
    if (!dietWrap) return;

    dietWrap.className = `mpb-card-diet${locked ? ' mpb-diet-locked' : ''}`;
    dietWrap.querySelectorAll('.mpb-diet-chip').forEach(chip => {
      chip.classList.toggle('active', this.isSelected && chip.dataset.tag === this.mealItem.dietaryPref);
      chip.disabled     = locked;
      chip.ariaDisabled = String(locked);
    });
  }

  // Refreshes the price span — called on onDurationChange and diet-chip change
  _updatePriceDisplay() {
    const wrap = this.el?.querySelector('.mpb-card-price-display');
    if (wrap) wrap.innerHTML = this._priceHtml();
  }

  // ─── State snapshot ───────────────────────────────────────────────────────────

  getState() {
    return {
      handle:     this.mealItem.handle,
      isSelected: this.isSelected,
      dietPref:   this.mealItem.dietaryPref,
    };
  }

  // ─── Cleanup ──────────────────────────────────────────────────────────────────
  // Must be called before removing from DOM to prevent listener leaks
  destroy() {
    document.removeEventListener('onDurationChange', this._onDurationChange);
    this.el = null;
  }

  // ─── Static: shared more-info modal ──────────────────────────────────────────
  static _openInfoModal(moreInfo) {
    let modal = document.getElementById('mpb-info-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id    = 'mpb-info-modal';
      modal.className = 'mpb-info-modal';
      modal.hidden = true;
      modal.innerHTML = `
        <div class="mpb-info-modal-backdrop"></div>
        <div class="mpb-info-modal-sheet" role="dialog" aria-modal="true">
          <button class="mpb-info-modal-close" type="button" aria-label="Close">&#x2715;</button>
          <div class="mpb-info-modal-header">
            <div class="mpb-info-modal-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="#056CF2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <circle cx="12" cy="16" r="0.6" fill="#056CF2" stroke="#056CF2" stroke-width="1.5"/>
              </svg>
            </div>
          </div>
          <div class="mpb-info-modal-content"></div>
        </div>`;
      document.body.appendChild(modal);
      modal.querySelector('.mpb-info-modal-backdrop')
           .addEventListener('click', () => { modal.hidden = true; });
      modal.querySelector('.mpb-info-modal-close')
           .addEventListener('click', () => { modal.hidden = true; });
      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && !modal.hidden) modal.hidden = true;
      });
    }
    modal.querySelector('.mpb-info-modal-content').innerHTML = moreInfo;
    modal.hidden = false;
  }
}